import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { CheckboxChipOption } from "../../../../../../../components/CheckboxChipOption/CheckboxChipOption";
import Dropdown from "../../../../../../../components/Dropdown/Dropdown";
import MultiSelectSearchableDropdown from "../../../../../../../components/MultiSelectSearchableDropdown/MultiSelectSearchableDropdown";
import SingleDatePicker from "../../../../../../../components/SingleDatePicker/SingleDatePicker";
import Input from "../../../../../../../components/Input/Input";
import TextArea from "../../../../../../../components/TextArea/TextArea";
import CalendarIcon from "../../../../../../../assets/svg/calendar-icon";
import ThreeDotsVerticalIcon from "../../../../../../../assets/svg/three-dots-vertical-icon";
import EditIcon from "../../../../../../../assets/svg/edit-icon";
import TrashIcon from "../../../../../../../assets/svg/trash-icon";
import { PermissionDisabledWrapper } from "../../PermissionDisabledWrapper";
import {
  type HelpdeskCustomFieldDefinition,
  HELPDESK_CUSTOM_FIELD_TYPES,
  getEffectiveDropdownOptions,
  getDefaultValueForFieldType,
  normalizeHelpdeskFieldType,
  orderCustomFieldsForRender,
} from "../../TicketDetails/components/TicketOverview/utils/helpdeskCustomFields";

const LABEL_CLASS =
  "text-md font-normal text-heading-text dark:text-heading-text-dk";

function createTicketFieldPlaceholder(
  normalizedType: ReturnType<typeof normalizeHelpdeskFieldType>
) {
  switch (normalizedType) {
    case HELPDESK_CUSTOM_FIELD_TYPES.TEXT:
      return "Enter text";
    case HELPDESK_CUSTOM_FIELD_TYPES.LONG_TEXT:
      return "Enter details";
    case HELPDESK_CUSTOM_FIELD_TYPES.NUMBER:
      return "Enter a number";
    default:
      return "";
  }
}

function optionNodes(label: string) {
  return {
    content: (
      <span className="text-md text-heading-text dark:text-heading-text-dk">
        {label}
      </span>
    ),
    selectedOptionContent: (
      <span className="text-md text-heading-text dark:text-heading-text-dk">
        {label}
      </span>
    ),
  };
}

function computeFieldActionMenuPosition(
  triggerRef: React.RefObject<HTMLElement | null>
) {
  if (!triggerRef.current) {
    return {
      x: 0,
      top: 0 as number | undefined,
      bottom: undefined as number | undefined,
    };
  }
  const rect = triggerRef.current.getBoundingClientRect();
  const popupWidth = 152;
  const popupHeight = 120;
  const gap = 4;

  const spaceBelow = window.innerHeight - rect.bottom - gap;
  const openAbove = spaceBelow < popupHeight && rect.top > spaceBelow;

  let x = rect.left;
  if (x < 4) x = 4;
  const maxLeft = window.innerWidth - popupWidth - 4;
  if (x > maxLeft) x = maxLeft;

  return {
    x,
    ...(openAbove
      ? { bottom: window.innerHeight - rect.top + gap, top: undefined }
      : { top: rect.bottom + gap, bottom: undefined }),
  };
}

function FieldActionMenu({
  field,
  onEdit,
  onDelete,
  disabled = false,
}: {
  field: HelpdeskCustomFieldDefinition;
  onEdit: (field: HelpdeskCustomFieldDefinition) => void;
  onDelete: (field: HelpdeskCustomFieldDefinition) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleToggleOpen = () => setOpen((p) => !p);

  const handleEditClick = () => {
    if (disabled) return;
    onEdit(field);
    setOpen(false);
  };

  const handleDeleteClick = () => {
    if (disabled) return;
    onDelete(field);
    setOpen(false);
  };

  const position = open ? computeFieldActionMenuPosition(triggerRef) : null;

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggleOpen}
        className="p-0.5 rounded hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors cursor-pointer mt-1.5"
      >
        <ThreeDotsVerticalIcon className="w-[16px] h-[16px] text-gray-placeholder dark:text-gray-placeholder-dk" />
      </button>

      {open &&
        position &&
        createPortal(
          <>
            <div
              className="fixed inset-0 overflow-hidden"
              style={{ zIndex: 9999998 }}
              onClick={() => setOpen(false)}
              onWheel={(e) => e.preventDefault()}
              onTouchMove={(e) => e.preventDefault()}
            />
            <div
              data-popup-content
              className="fixed w-[152px] p-2.5 rounded-lg bg-popper-bg-fixed-white dark:bg-popper-bg-fixed-white-dk border border-border-gray dark:border-border-gray-dk shadow-popper-blur-effect backdrop-blur-[20px]"
              style={{
                left: position.x,
                ...(position.bottom !== undefined
                  ? { bottom: position.bottom }
                  : { top: position.top }),
                zIndex: 9999999,
              }}
            >
              <PermissionDisabledWrapper
                disabled={disabled}
                className="w-max"
                tooltipSide="left"
              >
                <button
                  type="button"
                  onClick={handleEditClick}
                  className={`flex items-center gap-1 w-full rounded-[8px] p-2 hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors ${
                    disabled ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                >
                  <EditIcon className="w-[18px] h-[18px] text-heading-text dark:text-heading-text-dk" />
                  <span className="text-md font-normal text-heading-text dark:text-heading-text-dk">
                    Edit
                  </span>
                </button>
              </PermissionDisabledWrapper>
              <PermissionDisabledWrapper
                disabled={disabled}
                className="w-max"
                tooltipSide="left"
              >
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  className={`flex items-center gap-1 w-full rounded-[8px] p-2 hover:bg-tag-red-bg dark:hover:bg-tag-red-bg-dk transition-colors ${
                    disabled ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                >
                  <TrashIcon className="w-[18px] h-[18px] text-verba-red-alert dark:text-verba-red-alert-dk" />
                  <span className="text-md font-normal text-verba-red-alert dark:text-verba-red-alert-dk">
                    Delete
                  </span>
                </button>
              </PermissionDisabledWrapper>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}

export interface CreateTicketDynamicFieldsProps {
  fieldDefinitions: HelpdeskCustomFieldDefinition[] | undefined;
  values: Record<string, unknown>;
  onFieldChange: (fieldKey: string, value: unknown) => void;
  errors?: Record<string, string>;
  showErrors?: boolean;
  onEditField?: (field: HelpdeskCustomFieldDefinition) => void;
  onDeleteField?: (field: HelpdeskCustomFieldDefinition) => void;
  actionsDisabled?: boolean;
}

export function CreateTicketDynamicFields({
  fieldDefinitions,
  values,
  onFieldChange,
  errors,
  showErrors,
  onEditField,
  onDeleteField,
  actionsDisabled = false,
}: CreateTicketDynamicFieldsProps) {
  const ordered = orderCustomFieldsForRender(fieldDefinitions);

  const handleChange = (fieldKey: string, value: unknown) => {
    onFieldChange(fieldKey, value);
    const children = ordered?.filter((f) => f?.parent_field_key === fieldKey);
    for (const child of children) {
      onFieldChange(
        child?.key,
        getDefaultValueForFieldType(child?.type, child?.values_for_dropdown)
      );
    }
  };

  const fieldError = (key: string) =>
    showErrors && errors?.[key] ? (
      <span className="mt-1 text-xs text-verba-red-alert dark:text-verba-red-alert-dk">
        {errors[key]}
      </span>
    ) : null;

  if (!ordered?.length) return null;

  return (
    <div className="flex flex-col gap-4.5">
      <h3 className="text-base font-semibold text-heading-text dark:text-heading-text-dk">
        Custom Fields
      </h3>

      {ordered?.map((field) => {
        const t = normalizeHelpdeskFieldType(field?.type);
        const fieldKey = field?.key;

        const setThisField = (value: unknown) => handleChange(fieldKey, value);

        const handleEditFromMenu = (f: HelpdeskCustomFieldDefinition) => {
          onEditField?.(f);
        };
        const handleDeleteFromMenu = (f: HelpdeskCustomFieldDefinition) => {
          onDeleteField?.(f);
        };

        const labelEl = (
          <div className="flex items-center gap-1">
            <span className={LABEL_CLASS}>{field?.name}</span>
            {field?.is_required ? (
              <span className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk"></span>
            ) : (
              <span className="text-xs text-gray-placeholder dark:text-gray-placeholder-dk">
                (Optional)
              </span>
            )}
            {(onEditField || onDeleteField) && (
              <FieldActionMenu
                field={field}
                onEdit={handleEditFromMenu}
                onDelete={handleDeleteFromMenu}
                disabled={actionsDisabled}
              />
            )}
          </div>
        );

        if (t === HELPDESK_CUSTOM_FIELD_TYPES.DROPDOWN_SINGLE) {
          const options = getEffectiveDropdownOptions(field, values);
          const raw = values?.[field?.key];
          const current =
            typeof raw === "string" || raw === null || raw === undefined
              ? ((raw as string | null) ?? "")
              : String(raw);
          const validValue =
            current && options?.includes(current) ? current : null;

          return (
            <div
              key={field?.key}
              className="flex flex-col"
              data-cf-field-key={field?.key}
            >
              {labelEl}
              <Dropdown
                id={`create-cf-${field?.key}`}
                options={options?.map((opt) => ({
                  value: opt,
                  ...optionNodes(opt),
                }))}
                value={validValue}
                placeholder="Select an option"
                onChange={setThisField}
                className="w-full max-w-[270px]"
                triggerClassName="!bg-pure-white dark:!bg-pure-white-dk"
                selectedOptionContainerClassName="h-[36px] w-full"
                placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
                dropdownClassName="!max-h-[150px] mt-1"
                optionClassName="border-b border-border-gray dark:border-border-gray-dk"
                ariaLabel={field?.name}
              />
              {fieldError(field?.key)}
            </div>
          );
        }

        if (t === HELPDESK_CUSTOM_FIELD_TYPES.DROPDOWN_MULTI) {
          const options = getEffectiveDropdownOptions(field, values);
          const raw = values?.[field?.key];
          const selected = Array.isArray(raw)
            ? (raw as string[])
                ?.map(String)
                ?.filter((v) => options?.includes(v))
            : [];

          const multiOptions = options?.map((opt) => ({
            value: opt,
            label: opt,
            selectedOptionContent: opt,
            content: (
              <span className="text-md text-heading-text dark:text-heading-text-dk">
                {opt}
              </span>
            ),
          }));

          return (
            <div
              key={field?.key}
              className="flex flex-col"
              data-cf-field-key={field?.key}
            >
              {labelEl}
              <MultiSelectSearchableDropdown
                id={`create-cf-multi-${field?.key}`}
                options={multiOptions}
                value={selected}
                onChange={setThisField}
                placeholder="Select options"
                showSelectedOptionsChips={true}
                autoFitChips
                moreTagText=""
                showSearch={options?.length > 5}
                searchPlaceholder="Search..."
                className="w-full max-w-[270px]"
                placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
                triggerClassName="min-h-[36px] rounded-[8px] !bg-pure-white dark:!bg-pure-white-dk"
                inputContainerClassName="!py-0 h-full"
                dropdownOpenClassName="!max-h-[150px] mt-1"
                chipClassName="inline-flex items-center px-0 py-0 text-md font-medium rounded-none border-none bg-transparent text-heading-text dark:text-heading-text-dk shrink min-w-0 [&>button]:hidden after:content-[','] last:after:content-[''] after:ml-[1.5px]"
                moreTagClassName="inline-flex items-center px-1.5 py-0.5 text-xs rounded-md bg-tag-gray dark:bg-tag-gray-dk text-heading-text dark:text-heading-text-dk shrink-0 font-medium"
                ariaLabel={field?.name}
              />
              {fieldError(field?.key)}
            </div>
          );
        }

        if (t === HELPDESK_CUSTOM_FIELD_TYPES.RADIO) {
          const options = getEffectiveDropdownOptions(field, values);
          const raw = values?.[field?.key];
          const current = raw == null || raw === "" ? "" : String(raw);

          const selectRadioOption = (opt: string) => {
            setThisField(opt);
          };

          return (
            <div
              key={field?.key}
              className="flex flex-col w-full"
              data-cf-field-key={field?.key}
            >
              {labelEl}
              <div className="flex flex-col gap-2">
                {options?.map((opt) => (
                  <label
                    key={opt}
                    className="flex items-center gap-2.5 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name={`create-cf-radio-${field?.key}`}
                      checked={current === opt}
                      onChange={selectRadioOption.bind(null, opt)}
                      className="shrink-0 w-4 h-4 accent-verba-blue dark:accent-verba-blue-dk"
                    />
                    <span
                      className={`text-md text-heading-text dark:text-heading-text-dk ${
                        current === opt ? "font-semibold" : "font-normal"
                      }`}
                    >
                      {opt}
                    </span>
                  </label>
                ))}
              </div>
              {fieldError(field?.key)}
            </div>
          );
        }

        if (t === HELPDESK_CUSTOM_FIELD_TYPES.CHECKBOX) {
          const options = field?.values_for_dropdown ?? [];
          const raw = values?.[field?.key];

          if (!options?.length) {
            const checked = Boolean(raw);

            const onBooleanCheckboxChange = (
              e: React.ChangeEvent<HTMLInputElement>
            ) => {
              setThisField(e?.target?.checked);
            };

            return (
              <div
                key={field?.key}
                className="flex flex-col"
                data-cf-field-key={field?.key}
              >
                {labelEl}
                <label className="flex items-center gap-2 cursor-pointer w-fit">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={onBooleanCheckboxChange}
                    className="shrink-0 w-4 h-4 rounded accent-verba-blue dark:accent-verba-blue-dk"
                  />
                  <span className="text-md font-medium text-heading-text dark:text-heading-text-dk">
                    Yes
                  </span>
                </label>
                {fieldError(field?.key)}
              </div>
            );
          }

          const selected = Array.isArray(raw)
            ? (raw as string[])
                ?.map(String)
                ?.filter((v) => options?.includes(v))
            : [];

          const displayCards = field?.checkbox_display_style === "cards";

          const toggleOption = (opt: string) => {
            const next = selected?.includes(opt)
              ? selected?.filter((v) => v !== opt)
              : [...selected, opt];
            setThisField(next);
          };

          return (
            <div
              key={field?.key}
              className="flex flex-col w-full"
              data-cf-field-key={field?.key}
            >
              {labelEl}
              {displayCards ? (
                <div className="grid w-full grid-cols-1 gap-3 min-[520px]:grid-cols-3 items-start">
                  {options?.map((opt) => {
                    const isChecked = selected?.includes(opt);
                    return (
                      <CheckboxChipOption
                        key={opt}
                        variant="card"
                        checked={Boolean(isChecked)}
                        label={opt}
                        onChange={() => toggleOption(opt)}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-wrap items-start gap-2">
                  {options?.map((opt) => {
                    const isChecked = selected?.includes(opt);
                    return (
                      <CheckboxChipOption
                        key={opt}
                        variant="chip"
                        checked={Boolean(isChecked)}
                        label={opt}
                        onChange={() => toggleOption(opt)}
                      />
                    );
                  })}
                </div>
              )}
              {fieldError(field?.key)}
            </div>
          );
        }

        if (t === HELPDESK_CUSTOM_FIELD_TYPES.TEXT) {
          const v = values?.[field?.key];
          const str = v == null ? "" : String(v);

          const onTextInputChange = (
            e: React.ChangeEvent<HTMLInputElement>
          ) => {
            setThisField(e?.target?.value);
          };

          return (
            <div
              key={field?.key}
              className="flex flex-col"
              data-cf-field-key={field?.key}
            >
              {labelEl}
              <Input
                name={field?.key}
                value={str}
                onChange={onTextInputChange}
                placeholder={createTicketFieldPlaceholder(t)}
                secondaryContainerClassName="rounded-[8px] border border-border-gray dark:border-border-gray-dk focus-within:border-black dark:focus-within:border-white p-2 bg-pure-white dark:bg-pure-white-dk transition-colors max-w-[270px]"
                labelClassName="hidden"
              />
              {fieldError(field?.key)}
            </div>
          );
        }

        if (t === HELPDESK_CUSTOM_FIELD_TYPES.LONG_TEXT) {
          const v = values?.[field?.key];
          const str = v == null ? "" : String(v);

          const onLongTextChange = (
            e: React.ChangeEvent<HTMLTextAreaElement>
          ) => {
            setThisField(e?.target?.value);
          };

          return (
            <div
              key={field?.key}
              className="flex flex-col"
              data-cf-field-key={field?.key}
            >
              {labelEl}
              <TextArea
                name={field?.key}
                value={str}
                onChange={onLongTextChange}
                placeholder={createTicketFieldPlaceholder(t)}
                rows={3}
                labelClassName="hidden"
                containerClassName="w-full max-w-[270px]"
                secondaryContainerClassName="rounded-[8px] border border-border-gray dark:border-border-gray-dk focus-within:border-black dark:focus-within:border-white p-2 bg-pure-white dark:bg-pure-white-dk transition-colors"
              />
              {fieldError(field?.key)}
            </div>
          );
        }

        if (t === HELPDESK_CUSTOM_FIELD_TYPES.NUMBER) {
          const v = values?.[field?.key];
          const str = v == null || v === "" ? "" : String(v);

          const onNumberInputChange = (
            e: React.ChangeEvent<HTMLInputElement>
          ) => {
            const val = e?.target?.value;
            setThisField(val === "" ? "" : Number(val));
          };

          return (
            <div
              key={field?.key}
              className="flex flex-col"
              data-cf-field-key={field?.key}
            >
              {labelEl}
              <Input
                name={field?.key}
                type="number"
                value={str}
                onChange={onNumberInputChange}
                placeholder={createTicketFieldPlaceholder(t)}
                secondaryContainerClassName="rounded-[8px] border border-border-gray dark:border-border-gray-dk focus-within:border-black dark:focus-within:border-white p-2 bg-pure-white dark:bg-pure-white-dk transition-colors max-w-[200px]"
                labelClassName="hidden"
              />
              {fieldError(field?.key)}
            </div>
          );
        }

        if (t === HELPDESK_CUSTOM_FIELD_TYPES.DATE) {
          const raw = values?.[field?.key];
          const iso =
            typeof raw === "string" && raw
              ? raw
              : raw instanceof Date
                ? raw?.toISOString()
                : null;

          const onDateChange = (date: Date | null) => {
            setThisField(date ? date?.toISOString() : null);
          };

          return (
            <div
              key={field?.key}
              className="flex flex-col"
              data-cf-field-key={field?.key}
            >
              {labelEl}
              <SingleDatePicker
                value={iso ? new Date(iso) : null}
                onChange={onDateChange}
                Icon={CalendarIcon}
                iconClassName="w-4 h-4 text-gray-placeholder dark:text-gray-placeholder-dk"
                inputClassName="!gap-2 text-md font-normal text-heading-text dark:text-heading-text-dk h-[36px] rounded-[8px] border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk px-3 max-w-[200px]"
                calendarContainerClassName="rounded-[8px]"
                placeholder="Select date"
              />
              {fieldError(field?.key)}
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}
